import { ReactElement } from 'react'
import { useWatch } from 'react-hook-form'
import { Box, Stack, Typography } from '@mui/material'
import { InfoTooltip } from '@ui/feedback'
import {
  CheckSettingsFormFieldsCommonProps,
  compactCheckSettingsFormSx,
  getCheckSettingsMaxWidth,
  getCheckSettingsStackSpacing,
} from '../checkSettingsFormFieldLayout'
import { DefaultValues } from '../types'
import CrossSectionAxesSketch from './components/CrossSectionAxesSketch'
import EccentricityField from './components/EccentricityField'

const MEMBER_SETTINGS_ROOT = 'memberCheckSettingsSelectedElement' as const

interface Props extends CheckSettingsFormFieldsCommonProps {
  memberCheckSettingsSelectedPath?: string
}

/**
 * Eccentricity of the point loads on a rip or column, in the axes of the cross
 * section (stored in metres, edited in centimetres). e_y offsets the loads along
 * the width b (for a rip: along the wall) and bends about the z axis; e_z offsets
 * them in the direction of the height h (for a rip: perpendicular to the wall)
 * and bends about the y axis. The sign picks the side, shown in the
 * cross section sketch.
 */
const LoadEccentricityFormFields = ({
  disabled,
  compact,
  memberCheckSettingsSelectedPath = MEMBER_SETTINGS_ROOT,
}: Props): ReactElement | null => {
  const memberCheckSettingsSelectedElement: SettingsOnMember = useWatch<DefaultValues>({
    name: memberCheckSettingsSelectedPath as 'memberCheckSettingsSelectedElement',
  })

  if (!('load_eccentricity' in memberCheckSettingsSelectedElement))
    throw new Error('Load eccentricity not available')

  return (
    <Box
      data-cy="load-eccentricity-fields-container"
      maxWidth={getCheckSettingsMaxWidth(compact)}
      sx={compact ? compactCheckSettingsFormSx : undefined}
    >
      <Stack direction="column" spacing={getCheckSettingsStackSpacing(compact, 2)}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Lastexzentrizität der Punktlasten</Typography>
          <InfoTooltip text="Die Punktlasten (nicht das Eigengewicht) greifen um e versetzt zur Stabachse an. Das Versatzmoment N·e wirkt am Lastangriffspunkt, wird durch ein horizontales Kräftepaar N·e/l an Kopf- und Fußpunkt (konstante Querkraft) im Gleichgewicht gehalten und nimmt bis zum gelenkigen Fußpunkt linear auf Null ab; es wird in den Nachweisen „Biegung und Druck“ (mit Knicken) und „Schub“ berücksichtigt." />
        </Stack>
        <CrossSectionAxesSketch />
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <EccentricityField
            axis="y"
            loadEccentricityPath={`${memberCheckSettingsSelectedPath}.load_eccentricity`}
            disabled={disabled}
          />
          <EccentricityField
            axis="z"
            loadEccentricityPath={`${memberCheckSettingsSelectedPath}.load_eccentricity`}
            disabled={disabled}
          />
        </Stack>
      </Stack>
    </Box>
  )
}

export default LoadEccentricityFormFields
